import React, { useEffect, useMemo } from 'react';
import { ScrollView, useWindowDimensions, View } from 'react-native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { SentryErrorBoundary } from '../../shared/sentry/SentryErrorBoundary';
import {
  POS_MOBILE_CART_SHEET_LAYOUT,
  POS_MOBILE_CATALOG_HEADER_MAX_VIEWPORT_FRACTION,
} from '../application/posLayout.constants';
import { PosProductCatalogList } from '../components/catalog/PosProductCatalogList';
import { PosHeaderSection, type PosHeaderSectionProps } from '../components/catalog/PosHeaderSection';
import { PosCartSection } from '../components/cart/PosCartSection';
import { CartParkButton, CartClearButton } from '../components/cart/PosCartHeader';
import { PosMobileCartBar, PosMobileCartSheet } from '../components/PosMobileCartDock';
import { styles } from '../components/posShared.styles';
import { usePosScreenModule } from './usePosScreenModule';
import type { ProductSearchRow } from '../../catalog/domain-api';
import type { CartItem } from '../store/usePosStore';
import { PosCartProvider } from '../components/contexts/PosCartContext';
import { PosCatalogProvider } from '../components/contexts/PosCatalogContext';
import { PosScreenModals } from './PosScreenModals';
import { SplitBillModal } from '../components/cart/SplitBillModal';
import { PosOperationalAlertsBand } from '../components/PosOperationalAlertsBand';
import { useKitchenReadyAlerts } from '../../hospitality/providers/KitchenReadyAlertProvider';

export const PosScreen = () => {
  const { shared, catalog, cart, checkout, overlays, operationalAlerts } = usePosScreenModule();
  const { readyForPickup, dismissReady } = useKitchenReadyAlerts();
  const { layoutWide: isWidePosLayout } = useTabWorkspaceLayout();
  const { height: windowHeight } = useWindowDimensions();
  const { confirm: confirmClearCart, ConfirmModal: ClearCartConfirmModal } = useOperationalConfirm();
  const useMobileCartSheet = !isWidePosLayout && POS_MOBILE_CART_SHEET_LAYOUT;
  const mobileHeaderMaxHeight = useMemo(
    () => Math.round(windowHeight * POS_MOBILE_CATALOG_HEADER_MAX_VIEWPORT_FRACTION),
    [windowHeight],
  );
  const { productListRef, numColumns, catalogPaneStyle } = catalog;

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const operationalAlertsBand = (
    <PosOperationalAlertsBand
      mpQrBanner={operationalAlerts.mpQrBanner}
      onResumeMpQr={operationalAlerts.handleResumeMpQr}
      onOpenMpQrInVentas={operationalAlerts.handleOpenMpQrInVentas}
      readyForPickup={readyForPickup}
      onDismissReady={dismissReady}
    />
  );

  const catalogHeaderProps = {
    ...catalog.posCatalogHeaderProps,
    operationalAlertsBand,
  };

  const headerElement = isWidePosLayout ? (
    <PosHeaderSection {...catalogHeaderProps} deskPart="toolbar" />
  ) : (
    <PosHeaderSection {...catalogHeaderProps} />
  );

  const catalogContextValue = {
    productListRef,
    products: catalog.products,
    refreshing: catalog.refreshing,
    onRefresh: catalog.handleRefresh,
    contentContainerStyle: catalog.posCatalogListContentStyle,
    listEmpty: catalog.posCatalogEmptyProps,
    paginationDock: isWidePosLayout ? catalog.posCatalogPaginationDock : null,
    mobilePagination: !isWidePosLayout ? catalog.posCatalogMobilePagination : null,
    canEdit: shared.canEdit,
    onProductPress: catalog.handleProductPress,
    onPackagePress: catalog.handlePackagePress,
    onToggleProductFavorite: shared.canEdit
      ? (p: ProductSearchRow) => catalog.toggleProductFavorite(p.id)
      : undefined,
    isProductFavorite: (id: string) => catalog.isProductFavorite(id),
    productBadges: (id: string) => ({
      combo: catalog.isProductCombo(id) && catalog.railView !== 'combos',
      promo: catalog.productHasPromo(id) && catalog.railView !== 'promos',
    }),
    stockSalePolicy: catalog.stockSalePolicy,
    addedFlashProductId: catalog.addedFlashProductId,
    onCatalogListViewportHeight: catalog.onCatalogListViewportHeight,
    onCatalogListHeaderHeight: catalog.onCatalogListHeaderHeight,
    listHeader:
      isWidePosLayout && catalog.deskCatalogListHeaderActive
        ? ({ ...catalog.posCatalogHeaderProps, deskPart: 'catalogChrome' } satisfies PosHeaderSectionProps)
        : null,
  };

  const cartContextBase = {
    cart: cart.cart,
    total: checkout.total,
    discountTotal: checkout.discountTotal,
    cartEvaluation: cart.cartEvaluation,
    canEdit: shared.canEdit,
    isSaving: checkout.isSaving,
    onRemoveFromCart: cart.removeFromCart,
    onIncrementCartItem: cart.incrementCartItem,
    onSetCartLineQuantity: cart.setCartLineQuantity,
    onRemoveCartLine: cart.removeCartLine,
    onSetCartLineAttribution: cart.setCartLineAttribution,
    onSetCartLineNote: cart.setCartLineNote,
    onOpenMenuOptionsEditor: overlays.openMenuOptionsEditor,
    onOpenVariantEditor: overlays.openVariantEditor,
    onOpenVariosEditor: (line: CartItem) => overlays.openVariosEditor(line.lineId, line.name, line.price),
    showPromoActions: cart.showPromoActions,
    hasManualCodePromotions: cart.hasManualCodePromotions,
    hasCashierSelectPromotions: cart.hasCashierSelectPromotions,
    promoCodeInput: cart.promoCodeInput,
    onPromoCodeInputChange: cart.setPromoCodeInput,
    appliedPromoCode: cart.appliedPromoCode,
    appliedPromoCodes: cart.appliedPromoCodes,
    appliedManualPromotionId: cart.appliedManualPromotionId,
    appliedManualPromotionIds: cart.appliedManualPromotionIds,
    promoCodeError: cart.promoCodeError,
    onApplyPromoCode: cart.applyPromoCode,
    onClearAppliedPromoCode: cart.clearAppliedPromoCode,
    promoIsApplying: cart.isApplying,
    promoIsClearing: cart.isClearing,
    selectedCashierPromotionId: cart.selectedCashierPromotionId,
    cashierEligiblePromotions: cart.cashierEligiblePromotions,
    inactiveCashierPromoHints: cart.inactiveCashierPromoHints,
    onSelectCashierPromotion: cart.selectCashierPromotion,
    promoUiStatus: cart.promoUiStatus,
    onClearPromoSelection: cart.clearPromoSelection,
    canParkCurrentSale: cart.canParkCurrentSale,
    hasParkedSale: cart.hasParkedSale,
    parkedLineCount: cart.parkedSummary?.lineCount,
    parkedTotal: cart.parkedSummary?.total,
    onParkCurrentSale: () => void cart.parkCurrentSale(),
    onResumeParkedSale: () => void cart.resumeParkedSale(),
    onDiscardParkedSale: () => void cart.discardParkedSale(),
    stockWarningByProductId: checkout.stockWarningByProductId,
    stockWarningsLoading: checkout.stockWarningsLoading,
    onOpenScanner: catalog.openPosScannerOrManualCode,
    emptyCartScanLabel: overlays.barcodeAllowCamera ? 'Escanear' : 'Buscar código',
    onOpenVarious: () => overlays.setVariosVisible(true),
    selectedDiningTableName: checkout.selectedDiningTable?.name ?? null,
    onChangeDiningTable: checkout.hasTablesCapability ? checkout.openChangeDiningTablePicker : undefined,
    onSendCourseToKitchen: checkout.sendCourseToKitchen,
    canSendCourseToKitchen: checkout.canSendCourseToKitchen,
    onPrintPreCuenta: () => void checkout.printPreCuenta(),
    onSharePreCuenta: () => void checkout.sharePreCuenta(),
    canPrintPreCuenta: checkout.canPrintPreCuenta,
    printTicketIsPreCuenta: checkout.printTicketIsPreCuenta,
    printTicketCounterSummary: checkout.printTicketCounterSummary,
    onOpenSplitBill: checkout.openSplitBillModal,
    canSplitBill: checkout.canSplitBill,
  };

  const cartProviderValue = {
    ...cartContextBase,
    onOpenCheckout: () => void checkout.beginCheckout(),
  };

  const catalogList = (
    <PosCatalogProvider value={catalogContextValue}>
      <PosProductCatalogList numColumns={numColumns} />
    </PosCatalogProvider>
  );

  const mainSplit = (
    <View style={[styles.posMainLayout, isWidePosLayout && styles.posMainLayoutWide]}>
      <View style={catalogPaneStyle}>{catalogList}</View>
      {!useMobileCartSheet ? (
        <View style={[styles.posCartDock, isWidePosLayout && styles.posCartDockWide]}>
          <PosCartProvider value={cartProviderValue}>
            <PosCartSection linesScrollMaxHeight={cart.posCartLinesMaxHeight} />
          </PosCartProvider>
        </View>
      ) : null}
    </View>
  );

  return (
    <SentryErrorBoundary moduleName="POS">
    <ShellContentFrame style={styles.container}>
      {isWidePosLayout ? (
        <View style={styles.posDeskShell}>
          {headerElement}
          {mainSplit}
        </View>
      ) : (
        <>
          <ScrollView
            style={{ maxHeight: mobileHeaderMaxHeight, flexGrow: 0, flexShrink: 1 }}
            contentContainerStyle={{ flexGrow: 0 }}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            {headerElement}
          </ScrollView>
          {mainSplit}
        </>
      )}

      {useMobileCartSheet ? (
        <>
          <PosMobileCartBar
            itemCount={cart.cart.length}
            total={checkout.total}
            hasParkedSale={cart.hasParkedSale}
            canEdit={shared.canEdit}
            isSaving={checkout.isSaving}
            onOpenSheet={() => cart.setMobileCartSheetVisible(true)}
          />
          <PosMobileCartSheet
            visible={cart.mobileCartSheetVisible}
            onClose={() => cart.setMobileCartSheetVisible(false)}
            itemCount={cart.cart.length}
            headerRight={
              <>
                {shared.canEdit && cart.cart.length > 0 ? (
                  <CartClearButton
                    sheet
                    onPress={() =>
                      void confirmClearCart({
                        title: 'Vaciar carrito',
                        message: '¿Vacías todos los ítems?',
                        confirmLabel: 'Vaciar',
                        destructive: true,
                        iconName: 'trash-outline',
                      }).then((ok) => {
                        if (ok) checkout.clearCartIncludingTableSession();
                      })
                    }
                  />
                ) : null}
                {shared.canEdit && cart.canParkCurrentSale && !cart.hasParkedSale ? (
                  <CartParkButton sheet onPress={() => void cart.parkCurrentSale()} />
                ) : null}
              </>
            }
          >
            <PosCartProvider value={{ ...cartContextBase, onOpenCheckout: checkout.handlePosOpenCheckout }}>
              <PosCartSection inSheet />
            </PosCartProvider>
          </PosMobileCartSheet>
        </>
      ) : null}

      <PosScreenModals
        barcodeImport={{
          remoteImportVisible: overlays.remoteImportVisible,
          remoteImportHit: overlays.remoteImportHit,
          remoteImportSaving: overlays.remoteImportSaving,
          importError: overlays.importError,
          closeRemoteImport: overlays.closeRemoteImport,
          handleConfirmBarcodeImport: overlays.handleConfirmBarcodeImport,
          importCategoryOptions: catalog.importCategoryOptions,
          defaultImportCategoryId: overlays.defaultImportCategoryId,
        }}
        scanner={{
          scannerVisible: overlays.scannerVisible,
          manualBarcodeVisible: overlays.manualBarcodeVisible,
          barcodeAllowCamera: overlays.barcodeAllowCamera,
          setScannerVisible: overlays.setScannerVisible,
          setManualBarcodeVisible: overlays.setManualBarcodeVisible,
          handleScannerResult: overlays.handleScannerResult,
        }}
        dining={{
          diningTablePickerVisible: checkout.diningTablePickerVisible,
          diningTablePickerLoading: checkout.diningTablePickerLoading,
          diningTableOptions: checkout.diningTableOptions,
          confirmDiningTableAndCheckout: checkout.confirmDiningTableAndCheckout,
          setDiningTablePickerVisible: checkout.setDiningTablePickerVisible,
        }}
        categorySelector={{
          categorySelectorVisible: catalog.categorySelectorVisible,
          categoryOptions: catalog.categoryOptions,
          selectedCategory: catalog.selectedCategory,
          itemTypeFilter: catalog.itemTypeFilter,
          setCategorySelectorVisible: catalog.setCategorySelectorVisible,
          handleSelectCategoryFromMore: catalog.handleSelectCategoryFromMore,
        }}
        productPress={{
          stockTapNotice: overlays.stockTapNotice,
          setStockTapNotice: overlays.setStockTapNotice,
          stockTapNoticeCopy: overlays.stockTapNoticeCopy,
          handleAddOutOfStockProduct: overlays.handleAddOutOfStockProduct,
          openInventoryForProduct: overlays.openInventoryForProduct,
          weightProduct: overlays.weightProduct,
          setWeightProduct: overlays.setWeightProduct,
          hourProduct: overlays.hourProduct,
          setHourProduct: overlays.setHourProduct,
          variantPickerProduct: overlays.variantPickerProduct,
          setVariantPickerProduct: overlays.setVariantPickerProduct,
          variantPickerVariants: overlays.variantPickerVariants,
          setVariantPickerVariants: overlays.setVariantPickerVariants,
          pendingVariantId: overlays.pendingVariantId,
          setPendingVariantId: overlays.setPendingVariantId,
          handleVariantSelect: overlays.handleVariantSelect,
          menuOptionsProduct: overlays.menuOptionsProduct,
          setMenuOptionsProduct: overlays.setMenuOptionsProduct,
          menuOptionsGroups: overlays.menuOptionsGroups,
          menuOptionsLoading: overlays.menuOptionsLoading,
          initialMenuOptionsSelected: overlays.editingMenuOptions,
          initialMenuOptionsLineNote: overlays.editingLineNote,
          handleMenuOptionsConfirm: overlays.handleMenuOptionsConfirm,
          addProductToCart: cart.addProductToCart,
          currentBranchId: checkout.currentBranchId,
          stockSalePolicy: catalog.stockSalePolicy,
        }}
        checkoutModal={{
          cart: cart.cart,
          cartEvaluation: cart.cartEvaluation,
          promoUiStatus: cart.promoUiStatus,
          canOpenCashManagementFromPos: shared.canOpenCashManagementFromPos,
          allowFiscalSaleMode: shared.allowFiscalSaleMode,
          currentBranchId: checkout.currentBranchId,
          cashSessionReady: checkout.cashSessionReady,
          total: checkout.total,
          merchandiseTotal: checkout.merchandiseTotal,
          tipAmount: checkout.tipAmount,
          tipUiEnabled: checkout.tipUiEnabled,
          tipIntent: checkout.tipIntent,
          selectTipIntent: checkout.selectTipIntent,
          totalIngresado: checkout.totalIngresado,
          vuelto: checkout.vuelto,
          saldoPendiente: checkout.saldoPendiente,
          isSaving: checkout.isSaving,
          hasBlockingStockIssue: checkout.hasBlockingStockIssue,
          hasExpiredStock: checkout.hasExpiredStock,
          customerEligibleCustomers: checkout.customerEligibleCustomers,
          completeSale: checkout.completeSale,
          updateDraft: checkout.updateDraft,
          removeDraft: checkout.removeDraft,
          addSplitPayment: checkout.addSplitPayment,
          handleRequestStockCorrection: checkout.handleRequestStockCorrection,
          handleOpenCashManagementFromCheckout: checkout.handleOpenCashManagementFromCheckout,
          loadMoreCustomers: checkout.loadMoreCustomers,
          onSetCartLineAttribution: cart.setCartLineAttribution,
          overlayModal: checkout.CheckoutOverlayModal,
        }}
        mpQr={{
          currentBranchId: checkout.currentBranchId,
          userId: shared.userId,
          mercadopagoQrMethodAllowed: shared.mercadopagoQrMethodAllowed,
          clearPersistenceAfterSale: cart.clearPersistenceAfterSale,
          clearPendingSaleId: checkout.clearPendingSaleId,
          onMpQrApproved: operationalAlerts.refreshOperationalAlerts,
        }}
        scanMissSheet={overlays.scanMissSheetProps ?? null}
        confirmModals={{
          cartConfirm: cart.ConfirmModal,
          checkoutConfirm: checkout.ConfirmModal,
        }}
      />
      {ClearCartConfirmModal}
      {checkout.SupervisorPinModal}
      <SplitBillModal
        visible={checkout.splitBillModalVisible}
        lines={cart.cart}
        onClose={() => checkout.setSplitBillModalVisible(false)}
        onConfirm={(ids) => {
          checkout.confirmSplitBill(ids);
          // Defer so checkout open reads the replaced cart from the store.
          setTimeout(() => void checkout.beginCheckout(), 0);
        }}
      />
    </ShellContentFrame>
    </SentryErrorBoundary>
  );
};
